import React, { useEffect, useState } from 'react';
import { Modal, Row, Col, Form, Button, Alert, OverlayTrigger, Tooltip, InputGroup } from 'react-bootstrap';
import { useTenant } from '../../settings/TenantContext';
import { supabase } from '../../lib/supabaseClient';

export default function AutoLaunchModal({ show, onHide, onSave, admin = false, tenantId: propTenantId, siteId: propSiteId }) {
  const ctx = useTenant();
  const tenantId = propTenantId || ctx.tenantId;
  const siteId = propSiteId || ctx.siteId;
  const base = process.env.REACT_APP_SKINNY_SUPABASE_URL;

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const [enabled, setEnabled] = useState(false);
  const [launchDelaySec, setLaunchDelaySec] = useState(0);
  const [messageOrder, setMessageOrder] = useState('sequential'); // sequential | random
  const [frequency, setFrequency] = useState('once_per_session'); // once_per_session | once_per_day
  const [maxImpressions, setMaxImpressions] = useState(1);
  const [messages, setMessages] = useState([{ id: 'a1', text: '', delay_ms: 0 }, { id: 'a2', text: '', delay_ms: 2000 }]);

  const InfoIcon = ({ text }) => (
    <OverlayTrigger placement="right" delay={{ show: 150, hide: 0 }} overlay={<Tooltip id="autolaunch-tip" className="bg-dark">{text}</Tooltip>}>
      <button type="button" className="btn btn-outline-info btn-sm ms-1 p-0 d-inline-flex align-items-center justify-content-center rounded-circle" style={{ width: 18, height: 18, lineHeight: '16px' }} aria-label="Help">
        <i className="bi bi-info-circle" style={{ fontSize: 12 }} aria-hidden="true"></i>
      </button>
    </OverlayTrigger>
  );

  useEffect(() => {
    (async () => {
      if (!show || !tenantId || !siteId) return;
      setLoading(true);
      setError('');
      try {
        const url = `${(base || '').replace(/\/+$/,'')}/functions/v1/autolaunch-get?tenant_id=${encodeURIComponent(tenantId)}&site_id=${encodeURIComponent(siteId)}`;
        const res = await fetch(url, { method: 'GET' });
        if (!res.ok) {
          if (res.status === 404) {
            // defaults for new config
            setEnabled(false);
            setLaunchDelaySec(0);
            setMessageOrder('sequential');
            setFrequency('once_per_session');
            setMaxImpressions(1);
            setMessages([{ id: 'a1', text: '', delay_ms: 0 }, { id: 'a2', text: '', delay_ms: 2000 }]);
            setLoading(false);
            return;
          }
          const txt = await res.text().catch(() => '');
          let detail = txt;
          try { const j = JSON.parse(txt); if (j?.error || j?.message) detail = `${j.error || ''}${j.message ? `: ${j.message}` : ''}`; } catch (_) {}
          throw new Error(`HTTP ${res.status}${detail ? ` — ${String(detail).slice(0,160)}` : ''}`);
        }
        const data = await res.json();
        setEnabled(!!data.enabled);
        setLaunchDelaySec(Number(data.launch_delay_sec || 0));
        setMessageOrder(String(data.message_order || 'sequential'));
        // Map legacy "always" to "once_per_session" since Always mode is removed
        const loadedFreq = String(data.frequency || 'once_per_session');
        setFrequency(loadedFreq === 'always' ? 'once_per_session' : loadedFreq);
        setMaxImpressions(Number.isFinite(data.max_impressions_per_session) ? Number(data.max_impressions_per_session) : 1);
        const gm = Array.isArray(data.greet_messages) ? data.greet_messages : [];
        if (gm.length) setMessages(gm.map((m, i) => ({ id: m.id || `a${i+1}`, text: String(m.text || ''), delay_ms: Number(m.delay_ms || 0) })));
      } catch (e) {
        setError(e?.message || String(e));
      }
      setLoading(false);
    })();
  }, [show, tenantId, siteId, base]);

  function updateMsg(index, field, value) {
    setMessages((arr) => {
      const a = Array.isArray(arr) ? arr.slice() : [];
      if (!a[index]) a[index] = { id: `a${index+1}`, text: '', delay_ms: 0 };
      a[index] = { ...a[index], [field]: field === 'delay_ms' ? Number(value || 0) : value };
      return a;
    });
  }
  function addMsg() {
    setMessages((arr) => {
      const a = (arr || []).slice();
      if (a.length >= 3) return a;
      const id = `a${a.length + 1}`;
      a.push({ id, text: '', delay_ms: a.length === 0 ? 0 : 2000 });
      return a;
    });
  }
  function removeMsg(index) {
    setMessages((arr) => (arr || []).filter((_, i) => i !== index));
  }

  async function handleSave() {
    setSaving(true);
    setError('');
    try {
      const body = {
        tenant_id: tenantId,
        site_id: siteId,
        enabled: !!enabled,
        greet_messages: (messages || []).filter(m => (m.text || '').trim().length > 0).map(m => ({ id: m.id || '', text: String(m.text || ''), delay_ms: Number(m.delay_ms || 0) })),
        frequency: String(frequency || 'once_per_session'),
        cooldown_sec: null, // Always mode removed; cooldown no longer used
        launch_delay_sec: Number(launchDelaySec || 0),
        message_order: String(messageOrder || 'sequential'),
        max_impressions_per_session: Number(maxImpressions || 0),
      };
      if (admin) {
        const tok = (await supabase.auth.getSession()).data.session?.access_token || '';
        const { error } = await supabase.functions.invoke('admin-autolaunch-upsert', {
          body,
          headers: tok ? { Authorization: `Bearer ${tok}` } : undefined
        });
        if (error) throw error;
      } else {
        // Non-admin path: ensure current session token is sent (avoid implicit failures)
        const tok = (await supabase.auth.getSession()).data.session?.access_token || '';
        const headers = tok ? { Authorization: `Bearer ${tok}` } : undefined;
        const { error } = await supabase.functions.invoke('autolaunch-upsert', { body, headers });
        if (error) throw error;
      }
      onSave && onSave(body);
      onHide && onHide(true);
    } catch (e) {
      const name = (e && e.name) ? String(e.name) : '';
      const msg = (e && e.message) ? String(e.message) : String(e);
      setError(name ? `${name}: ${msg}` : msg);
    }
    setSaving(false);
  }

  function testOnThisPage() {
    try {
      const s = document.createElement('script');
      s.src = '/chatbot-widget.js';
      s.dataset.tenantId = tenantId;
      s.dataset.siteId = siteId;
      s.dataset.autoOpen = 'true';
      // Ensure chat-complete call has credentials during preview
      try {
        const pageCfg = (window && window.__SKINNY_SITE_CONFIG) || {};
        if (pageCfg && pageCfg.anonKey) s.dataset.anonKey = pageCfg.anonKey;
        if (pageCfg && pageCfg.apiKey) s.dataset.apiKey = pageCfg.apiKey;
        if (pageCfg && pageCfg.apiEndpoint) s.dataset.apiEndpoint = pageCfg.apiEndpoint;
      } catch (_) {}
      const m0 = messages[0]?.text || '';
      const m1 = messages[1]?.text || '';
      if (m0) s.dataset.greet1 = m0;
      if (m1) s.dataset.greet2 = m1;
      s.dataset.greetDelayMs = String(messages[1]?.delay_ms || 2000);
      s.dataset.launchDelaySec = String(launchDelaySec || 0);
      s.dataset.messageOrder = String(messageOrder || 'sequential');
      document.body.appendChild(s);
    } catch (_) {}
  }

  function resetGuards() {
    try {
      const t = (tenantId || 't');
      const s = (siteId || 's');
      const prefix = `SKNY_AUTO_${t}_${s}`;
      const lsKeys = [];
      const ssKeys = [];
      try {
        for (let i = 0; i < localStorage.length; i++) {
          const k = localStorage.key(i);
          if (k && k.indexOf(prefix) === 0) lsKeys.push(k);
        }
      } catch (_) {}
      try {
        for (let i = 0; i < sessionStorage.length; i++) {
          const k = sessionStorage.key(i);
          if (k && k.indexOf(prefix) === 0) ssKeys.push(k);
        }
      } catch (_) {}
      try { lsKeys.forEach((k) => localStorage.removeItem(k)); } catch (_) {}
      try { ssKeys.forEach((k) => sessionStorage.removeItem(k)); } catch (_) {}
      // eslint-disable-next-line no-console
    } catch (_) {}
  }

  return (
    <Modal show={!!show} onHide={() => onHide && onHide(false)} centered size="lg" enforceFocus={false}>
      <Modal.Header closeButton><Modal.Title>AutoGreet</Modal.Title></Modal.Header>
      <Modal.Body>
        {loading ? <div className="text-muted">Loading…</div> : (
          <>
            {error ? <Alert variant="danger">{error}</Alert> : null}
            <Row className="g-3">
              <Col md={4}>
                <Form.Check
                  type="switch"
                  id="auto-enabled"
                  label={<>Enable AutoGreet <InfoIcon text="When enabled, the chat opens automatically and sends your greeting message(s) based on the settings below." /></>}
                  checked={enabled}
                  onChange={(e) => setEnabled(!!(e && e.target && e.target.checked))}
                />
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.Label>
                    Launch Delay (seconds)
                    <InfoIcon text="Delay between page load and auto-open + greet." />
                  </Form.Label>
                  <Form.Control type="number" min={0} value={launchDelaySec} onChange={(e) => setLaunchDelaySec(Math.max(0, Number(e.target.value || 0)))} />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.Label>
                    Message Mode
                    <InfoIcon text="Sequential: show all greetings. Random: show one randomly chosen greeting per trigger." />
                  </Form.Label>
                  <Form.Select value={messageOrder} onChange={(e) => setMessageOrder(e.target.value)}>
                    <option value="sequential">Sequential (all)</option>
                    <option value="random">Random (one)</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            {enabled && (
              <>
                <Row className="g-3 mt-2">
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>
                        Frequency
                        <InfoIcon text="How often AutoGreet should trigger: Once per session (resets when browser closes) or Once per day (24-hour cooldown)." />
                      </Form.Label>
                      <Form.Select value={frequency} onChange={(e) => setFrequency(e.target.value)}>
                        <option value="once_per_session">Once per session</option>
                        <option value="once_per_day">Once per day</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                </Row>
                <Row className="g-3 mt-1">
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>
                        Max Impressions per Session
                        <InfoIcon text="Maximum times AutoGreet will show within a single browser session. Set 0 for unlimited." />
                      </Form.Label>
                      <Form.Control type="number" min={0} value={maxImpressions} onChange={(e) => setMaxImpressions(Math.max(0, Number(e.target.value || 0)))} />
                    </Form.Group>
                  </Col>
                </Row>
                <div className="mt-3">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <div className="fw-semibold">
                      Greeting Messages
                      <InfoIcon text="Up to 3 messages. Each can be delayed (ms). Sequential shows all; Random shows one." />
                    </div>
                    <Button variant="outline-secondary" size="sm" onClick={addMsg} disabled={messages.length >= 3}>Add Message</Button>
                  </div>
                  {messages.map((m, i) => (
                    <Row key={m.id || i} className="g-2 mb-2 align-items-end">
                      <Col md={7}>
                        <Form.Control as="textarea" rows={2} placeholder={`Greeting message ${i + 1}…`} value={m.text} onChange={(e) => updateMsg(i, 'text', e.target.value)} />
                      </Col>
                      <Col md={2}>
                        <InputGroup size="sm">
                          <Form.Control type="number" min={0} placeholder="Delay" value={m.delay_ms} onChange={(e) => updateMsg(i, 'delay_ms', e.target.value)} />
                          <InputGroup.Text>ms</InputGroup.Text>
                        </InputGroup>
                      </Col>
                      <Col md={3} className="d-flex gap-2">
                        <Button variant="outline-secondary" size="sm" onClick={() => removeMsg(i)} disabled={messages.length <= 1}>Remove</Button>
                      </Col>
                    </Row>
                  ))}
                </div>
              </>
            )}
          </>
        )}
      </Modal.Body>
      <Modal.Footer className="d-flex justify-content-between">
        <div className="d-flex gap-2">
          <Button variant="outline-secondary" onClick={resetGuards} disabled={!tenantId || !siteId || loading || saving}>Reset AutoGreet Guards</Button>
          <InfoIcon text="Clears AutoGreet session/localStorage flags (e.g., last shown time, impressions). Use this to re-trigger auto-open and greeting messages during testing." />
          {enabled && admin && <Button variant="outline-info" onClick={testOnThisPage} disabled={loading || saving}>Preview on this page</Button>}
        </div>
        <div className="d-flex gap-2">
          <Button variant="outline-secondary" onClick={() => onHide && onHide(false)}>Cancel</Button>
          <Button variant="primary" onClick={handleSave} disabled={saving || loading || !tenantId || !siteId}>{saving ? 'Saving…' : 'Save'}</Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
}

/*
import React, { useState, useEffect } from 'react';
import { Modal, Row, Col, Form, Button, Alert, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { useTenant } from '../../settings/TenantContext';
import { supabase } from '../../lib/supabaseClient';

export default function AutoLaunchModal({ show, onHide, onSave, admin = false, tenantId: propTenantId, siteId: propSiteId }) {
  const ctx = useTenant();
  const tenantId = propTenantId || ctx.tenantId;
  const siteId = propSiteId || ctx.siteId;
  const base = process.env.REACT_APP_SKINNY_SUPABASE_URL;
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [enabled, setEnabled] = useState(false);
  const [frequency, setFrequency] = useState('once_per_session');
  const [cooldownSec, setCooldownSec] = useState('');
  const [launchDelaySec, setLaunchDelaySec] = useState(0);
  const [messageOrder, setMessageOrder] = useState('sequential');
  const [messages, setMessages] = useState([{ id: 'a1', text: '', delay_ms: 0 }, { id: 'a2', text: '', delay_ms: 2000 }]);
  const [maxImpressions, setMaxImpressions] = useState(1);

  const InfoIcon = ({ text }) => (
    <OverlayTrigger placement="right" delay={{ show: 150, hide: 0 }} overlay={<Tooltip id="autolaunch-tip" className="bg-dark">{text}</Tooltip>}>
      <button type="button" className="btn btn-outline-info btn-sm ms-1 p-0 d-inline-flex align-items-center justify-content-center rounded-circle" style={{ width: 18, height: 18, lineHeight: '16px' }} aria-label="Help">
        <i className="bi bi-info-circle" style={{ fontSize: 12 }} aria-hidden="true"></i>
      </button>
    </OverlayTrigger>
  );

  useEffect(() => {
    (async () => {
      if (!show || !tenantId || !siteId) return;
      setLoading(true);
      setError('');
      try {
        // autolaunch-get only accepts GET - build URL and fetch directly
        const url = `${(base || '').replace(/\/+$/,'')}/functions/v1/autolaunch-get?tenant_id=${encodeURIComponent(tenantId)}&site_id=${encodeURIComponent(siteId)}`;
        // eslint-disable-next-line no-console
        try { console.info('[AutoLaunch][UI] GET autolaunch-get', { url, tenantId, siteId }); } catch (_) {}
        const res = await fetch(url, { method: 'GET', headers: { 'content-type': 'application/json' } });
        if (!res.ok) {
          if (res.status === 404) {
            // Initialize defaults when no config exists
            setEnabled(false);
            setFrequency('once_per_session');
            setCooldownSec('');
            setLaunchDelaySec(0);
            setMessageOrder('sequential');
            setMessages([{ id: 'a1', text: '', delay_ms: 0 }, { id: 'a2', text: '', delay_ms: 2000 }]);
            setLoading(false);
            return;
          }
          const txt = await res.text().catch(() => '');
          let detail = txt;
          try {
            const j = JSON.parse(txt);
            detail = j && (j.error || j.message) ? `${j.error || ''}${j.message ? `: ${j.message}` : ''}` : txt;
          } catch (_) {}
          try { console.warn('[AutoLaunch][UI] autolaunch-get failed', { status: res.status, body: txt }); } catch (_) {}
          throw new Error(`HTTP ${res.status}${detail ? ` — ${String(detail).slice(0,160)}` : ''}`);
        }
        const data = await res.json();
        setEnabled(!!data.enabled);
        setFrequency(String(data.frequency || 'once_per_session'));
        setCooldownSec(data.cooldown_sec == null ? '' : String(data.cooldown_sec));
        setLaunchDelaySec(Number(data.launch_delay_sec || 0));
        setMessageOrder(String(data.message_order || 'sequential'));
        setMaxImpressions(Number.isFinite(data.max_impressions_per_session) ? Number(data.max_impressions_per_session) : 1);
        const gm = Array.isArray(data.greet_messages) ? data.greet_messages : [];
        if (gm.length) setMessages(gm.map((m, i) => ({ id: m.id || `a${i+1}`, text: String(m.text || ''), delay_ms: Number(m.delay_ms || 0) })));
      } catch (err) {
        console.error('Failed to fetch AutoLaunch config:', err);
        setError(err?.message || String(err));
      }
      setLoading(false);
    })();
  }, [show, tenantId, siteId]);

  function updateMsg(index, field, value) {
    setMessages((arr) => {
      const a = Array.isArray(arr) ? arr.slice() : [];
      if (!a[index]) a[index] = { id: `a${index+1}`, text: '', delay_ms: 0 };
      a[index] = { ...a[index], [field]: field === 'delay_ms' ? Number(value || 0) : value };
      return a;
    });
  }

  function addMsg() {
    setMessages((arr) => {
      const a = (arr || []).slice();
      if (a.length >= 3) return a;
      const id = `a${a.length + 1}`;
      a.push({ id, text: '', delay_ms: a.length === 0 ? 0 : 2000 });
      return a;
    });
  }

  function removeMsg(index) {
    setMessages((arr) => (arr || []).filter((_, i) => i !== index));
  }

  async function handleSave() {
    setSaving(true);
    setError('');
    try {
      const body = {
        tenant_id: tenantId,
        site_id: siteId,
        enabled: !!enabled,
        greet_messages: (messages || []).filter(m => (m.text || '').trim().length > 0).map(m => ({ id: m.id || '', text: String(m.text || ''), delay_ms: Number(m.delay_ms || 0) })),
        frequency: String(frequency || 'once_per_session'),
        cooldown_sec: cooldownSec === '' ? null : Number(cooldownSec),
        launch_delay_sec: Number(launchDelaySec || 0),
        message_order: String(messageOrder || 'sequential'),
        max_impressions_per_session: Number(maxImpressions || 0),
      };

      if (admin) {
        const tok = (await supabase.auth.getSession()).data.session?.access_token || '';
        const { error } = await supabase.functions.invoke('admin-autolaunch-upsert', {
          body,
          headers: tok ? { Authorization: `Bearer ${tok}` } : undefined
        });
        if (error) throw error;
      } else {
        const { error } = await supabase.functions.invoke('autolaunch-upsert', {
          body
        });
        if (error) throw error;
      }

      onSave && onSave(body);
      onHide();
    } catch (err) {
      console.error('Failed to save AutoLaunch config:', err);
      setError(err?.message || String(err));
    }
    setSaving(false);
  }

  function testAutoLaunch() {
    try {
      // Local preview using global config override
      if (typeof window !== 'undefined') {
        window.__SKINNY_AUTO_OPEN = {
          enabled: true,
          greet_messages: messages.filter(m => (m.text || '').trim()),
          frequency: 'once_per_session',
          cooldown_sec: null,
          max_impressions_per_session: 1,
          launch_delay_sec: 1,
          message_order: messageOrder
        };
        const event = new CustomEvent('skinny:test-autolaunch');
        window.dispatchEvent(event);
      }
    } catch (err) {
      console.error('Test AutoLaunch failed:', err);
    }
  }

  return (
    <Modal show={!!show} onHide={() => onHide(false)} centered size="lg">
      <Modal.Header closeButton><Modal.Title>AutoLaunch Configuration</Modal.Title></Modal.Header>
      <Modal.Body>
        {loading ? <div className="text-muted">Loading…</div> : (
          <>
            {error ? <Alert variant="danger">{error}</Alert> : null}
            <Row className="g-3">
              <Col md={4}>
                <Form.Check
                  type="switch"
                  id="auto-enabled"
                  label={
                    <>
                      Enable AutoLaunch
                      <InfoIcon text="Turn on/off AutoLaunch for this site. When enabled, the chat widget will automatically open and display greeting messages based on your frequency settings." />
                    </>
                  }
                  checked={enabled}
                  onChange={(e) => setEnabled(!!(e && e.target && e.target.checked))}
                />
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.Label>
                    Launch Delay (seconds)
                    <InfoIcon text="The delay between page load and the chat opening and populating with messages. Use this to give visitors time to see the page before the chat appears." />
                  </Form.Label>
                  <Form.Control type="number" min={0} value={launchDelaySec} onChange={(e) => setLaunchDelaySec(Math.max(0, Number(e.target.value || 0)))} />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.Label>
                    Message Mode
                    <InfoIcon text="Sequential: shows all greeting messages in the order they are defined. Random: shows one randomly chosen greeting message per AutoLaunch trigger." />
                  </Form.Label>
                  <Form.Select value={messageOrder} onChange={(e) => setMessageOrder(e.target.value)}>
                    <option value="sequential">Sequential</option>
                    <option value="random">Random</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            {enabled && (
              <>
                <Row className="g-3 mt-2">
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label>
                        Frequency
                        <InfoIcon text="How often AutoLaunch should trigger: Once per session (default), Once per day, or Always (with cooldown)." />
                      </Form.Label>
                      <Form.Select value={frequency} onChange={(e) => setFrequency(e.target.value)}>
                        <option value="once_per_session">Once per session</option>
                        <option value="once_per_day">Once per day</option>
                        <option value="always">Always</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  {frequency === 'always' && (
                    <Col md={4}>
                      <Form.Group>
                        <Form.Label>
                          Cooldown (seconds)
                          <InfoIcon text="Minimum seconds before re-showing AutoLaunch for the same session/user. Only used when Frequency is set to Always." />
                        </Form.Label>
                        <Form.Control type="number" min={10} value={cooldownSec} onChange={(e) => setCooldownSec(e.target.value)} />
                      </Form.Group>
                    </Col>
                  )}
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label>
                        Max Impressions/Session
                        <InfoIcon text="Maximum times AutoLaunch will show within a single browser session. Set to 0 for unlimited." />
                      </Form.Label>
                      <Form.Control type="number" min={0} value={maxImpressions} onChange={(e) => setMaxImpressions(Math.max(0, Number(e.target.value || 0)))} />
                    </Form.Group>
                  </Col>
                </Row>

                <div className="mt-4">
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h6 className="mb-0">
                      Greeting Messages
                      <InfoIcon text="Up to 3 greeting messages that will be shown when AutoLaunch triggers. Each message can have a delay (in milliseconds) before it appears. Messages are shown in order or randomly based on Message Mode setting." />
                    </h6>
                    <Button variant="outline-secondary" size="sm" onClick={addMsg} disabled={messages.length >= 3}>
                      Add Message
                    </Button>
                  </div>

                  {messages.map((msg, i) => (
                    <Row key={msg.id || i} className="g-2 mb-2 align-items-end">
                      <Col md={7}>
                        <Form.Control
                          as="textarea"
                          rows={2}
                          placeholder={`Greeting message ${i + 1}...`}
                          value={msg.text}
                          onChange={(e) => updateMsg(i, 'text', e.target.value)}
                        />
                      </Col>
                      <Col md={2}>
                        <InputGroup size="sm">
                          <Form.Control
                            type="number"
                            placeholder="Delay"
                            min={0}
                            value={msg.delay_ms}
                            onChange={(e) => updateMsg(i, 'delay_ms', e.target.value)}
                          />
                          <InputGroup.Text>ms</InputGroup.Text>
                        </InputGroup>
                      </Col>
                      <Col md={3} className="d-flex gap-1">
                        <Button variant="outline-secondary" size="sm" onClick={() => removeMsg(i)} disabled={messages.length <= 1}>
                          Remove
                        </Button>
                      </Col>
                    </Row>
                  ))}
                </div>
              </>
            )}
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={() => onHide(false)}>
          Cancel
        </Button>
        {enabled && (
          <Button variant="outline-info" onClick={testAutoLaunch}>
            Test on This Page
          </Button>
        )}
        <Button variant="primary" onClick={handleSave} disabled={saving}>
          {saving ? 'Saving...' : 'Save Settings'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
*/
